import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UI, btnSx } from "../components/ActionDrawer";
import DashTable, { Oid, PageHead, Sub2, Tag } from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const PAGE_SIZE = 15;

/* Ops Lead's view of everything dispatched, with installation status. */
const ShippedOrders = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [opsLeadSummary, setOpsLeadSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchRows = async (targetPage = page) => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const [shippedRes, summaryRes] = await Promise.all([
        apiService.fetchShippedOrders(token, { page: targetPage, limit: PAGE_SIZE }),
        apiService.fetchOpsLeadSummary(token),
      ]);
      setRows(shippedRes.data || []);
      setSummary(shippedRes.summary || null);
      setPagination(shippedRes.pagination || null);
      setOpsLeadSummary(summaryRes.data || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching shipped orders");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const columns = [
    { label: "Clinic" },
    { label: "City" },
    { label: "Serial" },
    { label: "Dispatched" },
    { label: "Technician" },
    { label: "Arrival" },
    { label: "Status" },
  ];

  const tableRows = rows.map((row) => ({
    id: row.orderId,
    cells: [
      <>
        {row.clinicName}
        <Sub2>{row.doctorName}</Sub2>
      </>,
      row.city,
      row.serial ? <Oid>{row.serial}</Oid> : <Tag tone="i">Not allocated</Tag>,
      shortDate(row.dispatchedAt),
      row.technicianName || <Tag tone="b">Unassigned</Tag>,
      row.arrivalDate ? shortDate(row.arrivalDate) : "—",
      row.stage === "Activated" ? (
        <Tag tone="g">Activated</Tag>
      ) : row.stage === "Installation" ? (
        <Tag tone="s">Installing</Tag>
      ) : (
        <Tag tone="bl">In transit</Tag>
      ),
    ],
  }));

  /* Shared across every Ops Lead page — not recomputed per page. */
  const kpis = opsLeadSummary
    ? [
        { label: "Needs your action", value: opsLeadSummary.needsAction },
        { label: "Blocking", value: opsLeadSummary.blocking, alarm: true },
        { label: "Jobs in flight", value: opsLeadSummary.jobsInFlight },
        { label: "Unassigned", value: opsLeadSummary.unassigned },
      ]
    : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="Shipped orders"
        lede="Everything dispatched, with installation status."
        kpis={kpis}
      />

      <DashTable
        columns={columns}
        rows={tableRows}
        onRowClick={(id) => navigate(`/dashboard/orders/${id}`)}
        emptyText="Nothing shipped yet."
        errorText={loadFailed ? "Could not load shipped orders." : null}
        onRetry={fetchRows}
        foot={
          summary
            ? `${summary.shipped ?? rows.length} shipped · ${
                summary.awaitingTechnician ?? 0
              } awaiting a technician`
            : null
        }
      />

      {pagination && pagination.total > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "10px",
            fontSize: 13,
            color: UI.ink3,
          }}
        >
          <span>
            {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total}
          </span>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={pagination.page <= 1}
                sx={btnSx("ghost", pagination.page <= 1)}
              >
                Previous
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                disabled={pagination.page >= pagination.totalPages}
                sx={btnSx("ghost", pagination.page >= pagination.totalPages)}
              >
                Next
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default ShippedOrders;
